import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useMemo, useState } from "react";
import type { FilterToggle } from "../../../shared-components/fhir-sections/fhir-section";
import { BaseTableLayout, ColumnDef } from "../../components/base-table-layout";
import ToggleFilter from "../../components/toggle-filter";
import { TableRow } from "../../utils/tables-metadata";
import { CarePlanRowData } from "./care-plan-rows";

export type CarePlanTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
};

export function CarePlanTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Care Plans",
}: CarePlanTableProps) {
  const [activeFilters, setActiveFilters] = useState<FilterToggle[]>([
    {
      label: "Upcoming Only",
      isChecked: true,
      filterFn: (rowData: CarePlanRowData) => rowData.isUpcoming,
    },
  ]);

  const filterToggles: FilterToggle[] = [
    {
      label: "Upcoming Only",
      isChecked: true,
      filterFn: (rowData: CarePlanRowData) => rowData.isUpcoming,
    },
  ];

  const filteredRows = useMemo(() => {
    const checkedFilters = activeFilters.filter(f => f.isChecked);
    if (checkedFilters.length === 0) return rows;

    return rows.filter(row => {
      return checkedFilters.some(filter => filter.filterFn(row));
    });
  }, [rows, activeFilters]);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  const columns: ColumnDef<CarePlanRowData>[] = useMemo(
    () => [
      { key: "description", header: "Description" },
      { key: "practitionerName", header: "Practitioner" },
      { key: "locationDetails", header: "Location" },
      { key: "authorOrganizationDetails", header: "Author Organization" },
      { key: "date", header: "Date", sort: "desc", width: "150px" },
    ],
    []
  );

  return (
    <BaseTableLayout
      title={displayName}
      rows={filteredRows as unknown as CarePlanRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      headerActionsSlot={
        <ToggleFilter
          filters={filterToggles}
          activeFilters={activeFilters}
          toggleFilter={(label: string, isChecked: boolean) => {
            if (isChecked) {
              const newFilter = filterToggles.find(f => f.label === label);
              if (newFilter) {
                setActiveFilters([{ ...newFilter, isChecked: true }]);
              }
            } else {
              setActiveFilters([]);
            }
          }}
        />
      }
    />
  );
}
